import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setMainHeading } from '../../../redux/modules/mainHeading/slice';
import { getMyLabResultsRequest, resetGetMyLabResults } from '../../../redux/modules/laboratory/labReport/slice';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress, Accordion, AccordionSummary, AccordionDetails, useMediaQuery, useTheme, Stack, Card, CardContent, Divider } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const flagColors = {
    'H': 'warning', 'L': 'warning', 'HH': 'error', 'LL': 'error',
    'A': 'warning', 'AA': 'error', 'N': 'success',
};

export default function MyLabResults() {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { getMyLabResultsRes, loading } = useSelector(state => state.labReport);

    useEffect(() => {
        dispatch(setMainHeading("My Lab Results"));
        dispatch(getMyLabResultsRequest());
        return () => { dispatch(resetGetMyLabResults()); };
    }, [dispatch]);

    const data = getMyLabResultsRes?.data;
    const tests = data?.tests || [];

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    if (!data || tests.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary">No lab results available</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Your lab results will appear here once they are finalized and released.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 2, sm: 3 }, px: { xs: 0.5, sm: 0 } }}>
                Showing your finalized lab results. Contact the laboratory if you have questions about your results.
            </Typography>

            {tests.map(testGroup => (
                <Accordion key={testGroup.testId} defaultExpanded={false} variant="outlined" sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: isMobile ? 'flex-start' : 'center',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: isMobile ? 0.5 : 2,
                            width: '100%',
                            overflow: 'hidden',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: isMobile ? 'none' : 'unset' }}>
                                <Typography sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {testGroup.testName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                    {testGroup.testCode}
                                </Typography>
                            </Box>
                            {!isMobile && <Box sx={{ flexGrow: 1 }} />}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {testGroup.results.length} result{testGroup.results.length !== 1 ? 's' : ''}
                                </Typography>
                                {testGroup.results[0]?.flag && testGroup.results[0].flag !== 'N' && (
                                    <Chip
                                        label={`Latest: ${testGroup.results[0].value} ${testGroup.unit || ''}`}
                                        color={flagColors[testGroup.results[0].flag] || 'default'}
                                        size="small"
                                    />
                                )}
                            </Box>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: isMobile ? 1 : 2 }}>
                        {isMobile ? (
                            /* Mobile: Card layout for results */
                            <Stack spacing={1.5}>
                                {testGroup.results.map((r, i) => (
                                    <Card key={r._id || i} variant="outlined" sx={{ borderLeft: 3, borderColor: r.isCritical ? 'error.main' : (r.flag && r.flag !== 'N' ? 'warning.main' : 'divider') }}>
                                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {r.date ? new Date(r.date).toLocaleDateString('en-GB') : '-'}
                                                </Typography>
                                                {r.flag && r.flag !== 'N' ? (
                                                    <Chip label={r.flag} color={flagColors[r.flag] || 'default'} size="small" />
                                                ) : null}
                                            </Box>
                                            <Typography sx={{
                                                fontWeight: r.flag && r.flag !== 'N' ? 700 : 500,
                                                fontSize: '1.1rem',
                                                color: r.isCritical ? 'error.main' : (r.flag && r.flag !== 'N' ? 'warning.main' : 'text.primary')
                                            }}>
                                                {r.value ?? '-'} {testGroup.unit || ''}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        ) : (
                            /* Desktop: Table layout */
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Order</TableCell>
                                            <TableCell align="right">Result</TableCell>
                                            <TableCell>Unit</TableCell>
                                            <TableCell>Flag</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {testGroup.results.map((r, i) => (
                                            <TableRow key={r._id || i}>
                                                <TableCell>{r.date ? new Date(r.date).toLocaleDateString('en-GB') : '-'}</TableCell>
                                                <TableCell>{r.orderId || '-'}</TableCell>
                                                <TableCell align="right" sx={{
                                                    fontWeight: r.flag && r.flag !== 'N' ? 700 : 400,
                                                    color: r.isCritical ? 'error.main' : (r.flag && r.flag !== 'N' ? 'warning.main' : 'text.primary')
                                                }}>
                                                    {r.value ?? '-'}
                                                </TableCell>
                                                <TableCell>{testGroup.unit || '-'}</TableCell>
                                                <TableCell>
                                                    {r.flag && r.flag !== 'N' ? (
                                                        <Chip label={r.flag} color={flagColors[r.flag] || 'default'} size="small" />
                                                    ) : '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
}
